"use client";

// OrganismFrostSensor — the system-wide half of dynamic rendering. Modal
// overlays frost the organism with a full-viewport backdrop-filter, and a
// backdrop blur re-runs every frame its backdrop repaints — so a live organism
// under an open modal is the same reader-view lag, everywhere a modal opens.
//
// Rather than wire every modal by hand, this ONE sensor (mounted once at the
// app root) watches for any frost overlay entering the DOM and rests the
// organism (calm bus) while one is present. It keys on `.os-modal-backdrop`
// (the shared modal scrim class used by Sheet, StepShell, MediaModal, the
// connectors/settings/context sheets, feedback, …) plus an opt-in
// `[data-organism-frost]` hook for any future custom overlay. Reading scrims
// are NOT matched here — they are always mounted (opacity 0 when idle) and drive
// calm imperatively from their own scroll ramp instead.
//
// The observer is coalesced to one cheap class lookup per animation frame, so
// even on a busy surface it costs microseconds; modals open rarely.

import { useEffect } from "react";
import { setOrganismCalm } from "../lib/organism/calmBus";

// the veiled guide window (`.cm-stage`) is the exception: netti is lifted IN
// FRONT of its veil, so nothing re-blurs over the creature and it stays awake.
// A sheet on its way OUT (`data-state="exit"`) no longer counts: netti comes
// back to full resolution the frame the veil starts to thin, never after it has
// (futureOrganism REST_DEEP_AT: the small version is never seen).
const FROST_SELECTOR = '.os-modal-backdrop:not(.cm-stage), [data-organism-frost]:not([data-state="exit"])';

export default function OrganismFrostSensor() {
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const present = !!document.querySelector(FROST_SELECTOR);
      // full rest — a modal's blur fully covers the organism, so it drops to an
      // even half-rate beat and, once the veil has settled, a fraction of its
      // pixels (futureOrganism, rest mode). The blur then re-runs on half the
      // frames, over a far cheaper backdrop.
      setOrganismCalm("modal-frost", present ? 1 : null);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    check(); // catch a modal that's already open at mount
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => {
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
      setOrganismCalm("modal-frost", null);
    };
  }, []);
  return null;
}
